import { level1 } from '@/levels/level1';
import type { LevelData, PlatformData } from '@/types';
import {
  COIN_HEIGHT,
  COIN_WIDTH,
  COYOTE_TICKS,
  GOAL_HEIGHT,
  GOAL_WIDTH,
  GRAVITY,
  GRAVITY_ASCENT_TICK_INTERVAL,
  GRAVITY_DESCENT_TICK_INTERVAL,
  JETPACK_FUEL_REGEN_TICK_INTERVAL,
  JETPACK_MAX_ASCENT_SPEED,
  JETPACK_MAX_FUEL,
  JETPACK_THRUST_FORCE,
  JETPACK_THRUST_TICK_INTERVAL,
  JUMP_BUFFER_TICKS,
  LASER_COOLDOWN_TICKS,
  LASER_DAMAGE,
  LASER_HEIGHT,
  LASER_LIFETIME_TICKS,
  LASER_SPEED,
  LASER_WIDTH,
  MAX_HEALTH,
  PLAYER_HEIGHT,
  PLAYER_JUMP_FORCE,
  PLAYER_SPEED,
  PLAYER_START_LEFT,
  PLAYER_WIDTH,
  REQUIRED_COINS_TO_LAUNCH,
  TERMINAL_VELOCITY,
} from './constants';
import { deriveInputEdges, mirrorX, overlaps } from './helpers';
import type {
  CoinSimState,
  FrameInputState,
  PlayerSimState,
  ProjectileState,
  Rect,
  SimState,
} from './types';

interface WinResolution {
  status: 'won' | 'draw';
  winnerId: number | null;
  winReason: 'elimination' | 'rocket' | 'draw';
}

function playerRect(player: PlayerSimState): Rect {
  return {
    x: player.x,
    y: player.y,
    width: player.width,
    height: player.height,
  };
}

function projectileRect(projectile: ProjectileState): Rect {
  return {
    x: projectile.x - Math.trunc(projectile.width / 2),
    y: projectile.y - Math.trunc(projectile.height / 2),
    width: projectile.width,
    height: projectile.height,
  };
}

function goalRect(state: SimState): Rect {
  return {
    x: state.goal.x,
    y: state.goal.y,
    width: state.goal.width,
    height: state.goal.height,
  };
}

function canJump(player: PlayerSimState): boolean {
  return player.isGrounded || player.coyoteTimerTicks > 0;
}

function clampPlayerToStage(player: PlayerSimState, stageWidth: number, stageHeight: number): void {
  if (player.x < 0) {
    player.x = 0;
    player.velocityX = 0;
  }
  if (player.x + player.width > stageWidth) {
    player.x = stageWidth - player.width;
    player.velocityX = 0;
  }
  if (player.y < 0) {
    player.y = 0;
    player.velocityY = 0;
  }
  if (player.y + player.height > stageHeight) {
    player.y = stageHeight - player.height;
    player.velocityY = 0;
    player.isGrounded = true;
  }
}

function resolvePlatformCollision(player: PlayerSimState, platforms: PlatformData[]): void {
  const wasGrounded = player.isGrounded;
  player.isGrounded = false;

  const prevX = player.x - player.velocityX;
  const prevY = player.y - player.velocityY;
  const prevTop = prevY;
  const prevBottom = prevY + player.height;
  const prevLeft = prevX;
  const prevRight = prevX + player.width;

  let landingY: number | null = null;
  let ceilingY: number | null = null;

  for (const platform of platforms) {
    const platformLeft = platform.x;
    const platformRight = platform.x + platform.width;
    const platformTop = platform.y;
    const platformBottom = platform.y + platform.height;

    const currentTop = player.y;
    const currentBottom = player.y + player.height;
    const currentLeft = player.x;
    const currentRight = player.x + player.width;

    const prevHorizontalOverlap = prevRight > platformLeft && prevLeft < platformRight;
    const currentHorizontalOverlap = currentRight > platformLeft && currentLeft < platformRight;
    const horizontalOverlap = prevHorizontalOverlap || currentHorizontalOverlap;

    // Top-face landing uses previous and current bounds to prevent tunneling.
    if (
      player.velocityY >= 0 &&
      horizontalOverlap &&
      prevBottom <= platformTop &&
      currentBottom >= platformTop
    ) {
      const candidate = platformTop - player.height;
      if (landingY === null || candidate < landingY) {
        landingY = candidate;
      }
      continue;
    }

    // Ceiling hit is resolved similarly for upward movement.
    if (
      player.velocityY < 0 &&
      horizontalOverlap &&
      prevTop >= platformBottom &&
      currentTop <= platformBottom
    ) {
      const candidate = platformBottom;
      if (ceilingY === null || candidate > ceilingY) {
        ceilingY = candidate;
      }
      continue;
    }

    const a: Rect = { x: player.x, y: player.y, width: player.width, height: player.height };
    const b: Rect = { x: platform.x, y: platform.y, width: platform.width, height: platform.height };
    if (!overlaps(a, b)) continue;

    const overlapX = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
    const overlapY = Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y);

    if (overlapX <= overlapY) {
      if (player.velocityX > 0 || prevRight <= platformLeft) {
        player.x = platformLeft - player.width;
      } else if (player.velocityX < 0 || prevLeft >= platformRight) {
        player.x = platformRight;
      } else {
        const playerCenterX = player.x + Math.trunc(player.width / 2);
        const platformCenterX = platform.x + Math.trunc(platform.width / 2);
        player.x = playerCenterX < platformCenterX ? platformLeft - player.width : platformRight;
      }
      player.velocityX = 0;
    } else if (player.velocityY >= 0) {
      player.y = platformTop - player.height;
      player.velocityY = 0;
      player.isGrounded = true;
    } else {
      player.y = platformBottom;
      player.velocityY = 0;
    }
  }

  if (landingY !== null) {
    player.y = landingY;
    player.velocityY = 0;
    player.isGrounded = true;
  } else if (ceilingY !== null) {
    player.y = ceilingY;
    player.velocityY = 0;
    player.gravityTickCounter = 0;
  }

  if (player.isGrounded) {
    player.gravityTickCounter = 0;
  }

  if (wasGrounded && !player.isGrounded && player.velocityY >= 0) {
    player.coyoteTimerTicks = COYOTE_TICKS;
  }
}

function updatePlayerMovementAndActions(
  state: SimState,
  player: PlayerSimState,
  input: FrameInputState
): void {
  if (player.health <= 0) {
    player.velocityX = 0;
    player.velocityY = 0;
    player.isGrounded = false;
    player.prevJumpHeld = input.jumpHeld;
    player.prevFireHeld = input.fireHeld;
    return;
  }

  if (player.coyoteTimerTicks > 0) player.coyoteTimerTicks--;
  if (player.jumpBufferTimerTicks > 0) player.jumpBufferTimerTicks--;
  if (player.laserCooldownTicks > 0) player.laserCooldownTicks--;

  const edges = deriveInputEdges(player.prevJumpHeld, player.prevFireHeld, input);

  if (input.left && !input.right) {
    player.velocityX = -PLAYER_SPEED;
    player.isFacingRight = false;
  } else if (input.right && !input.left) {
    player.velocityX = PLAYER_SPEED;
    player.isFacingRight = true;
  } else {
    player.velocityX = 0;
  }

  if (edges.jumpPressed) {
    player.jumpBufferTimerTicks = JUMP_BUFFER_TICKS;
  }
  if (player.jumpBufferTimerTicks > 0 && canJump(player)) {
    player.velocityY = PLAYER_JUMP_FORCE;
    player.isGrounded = false;
    player.coyoteTimerTicks = 0;
    player.gravityTickCounter = 0;
    player.jumpBufferTimerTicks = 0;
  }

  if (edges.firePressed && player.laserCooldownTicks <= 0) {
    player.laserCooldownTicks = LASER_COOLDOWN_TICKS;
    state.projectiles.push({
      id: state.nextProjectileId++,
      ownerId: player.id,
      x: player.isFacingRight ? player.x + player.width + 2 : player.x - 2,
      y: player.y + Math.trunc(player.height / 2),
      velocityX: LASER_SPEED * (player.isFacingRight ? 1 : -1),
      ttl: LASER_LIFETIME_TICKS,
      damage: LASER_DAMAGE,
      width: LASER_WIDTH,
      height: LASER_HEIGHT,
    });
  }

  const canUseJetpack = input.jetpackHeld && !player.isGrounded && player.jetpackFuel > 0;
  if (canUseJetpack) {
    player.jetpackFuel = Math.max(0, player.jetpackFuel - 1);
    player.jetpackFuelRegenTickCounter = 0;
    player.jetpackThrustTickCounter++;
    if (player.jetpackThrustTickCounter >= JETPACK_THRUST_TICK_INTERVAL) {
      player.velocityY = Math.max(player.velocityY - JETPACK_THRUST_FORCE, JETPACK_MAX_ASCENT_SPEED);
      player.jetpackThrustTickCounter = 0;
    }
  } else {
    player.jetpackThrustTickCounter = 0;
    if (player.jetpackFuel < player.maxJetpackFuel) {
      player.jetpackFuelRegenTickCounter++;
      if (player.jetpackFuelRegenTickCounter >= JETPACK_FUEL_REGEN_TICK_INTERVAL) {
        player.jetpackFuel = Math.min(player.maxJetpackFuel, player.jetpackFuel + 1);
        player.jetpackFuelRegenTickCounter = 0;
      }
    } else {
      player.jetpackFuelRegenTickCounter = 0;
    }
  }

  const gravityInterval = player.velocityY < 0 ? GRAVITY_ASCENT_TICK_INTERVAL : GRAVITY_DESCENT_TICK_INTERVAL;
  player.gravityTickCounter++;
  if (player.gravityTickCounter >= gravityInterval) {
    player.velocityY = Math.min(player.velocityY + GRAVITY, TERMINAL_VELOCITY);
    player.gravityTickCounter = 0;
  }

  player.x += player.velocityX;
  player.y += player.velocityY;

  resolvePlatformCollision(player, state.platforms);
  clampPlayerToStage(player, state.stageWidth, state.stageHeight);

  player.prevJumpHeld = input.jumpHeld;
  player.prevFireHeld = input.fireHeld;
}

function updateProjectiles(state: SimState): void {
  const active: ProjectileState[] = [];
  const playersById = [...state.players].sort((a, b) => a.id - b.id);

  for (const projectile of state.projectiles) {
    const updated: ProjectileState = { ...projectile };
    updated.x += updated.velocityX;
    updated.ttl--;

    if (updated.ttl <= 0) continue;

    const bounds = projectileRect(updated);
    if (bounds.x + bounds.width < 0 || bounds.x > state.stageWidth) continue;

    let hitPlatform = false;
    for (const platform of state.platforms) {
      if (overlaps(bounds, platform)) {
        hitPlatform = true;
        break;
      }
    }
    if (hitPlatform) continue;

    let hitPlayer = false;
    for (const player of playersById) {
      if (player.id === updated.ownerId) continue;
      if (player.health <= 0) continue;
      if (overlaps(bounds, playerRect(player))) {
        player.health = Math.max(0, player.health - updated.damage);
        hitPlayer = true;
        break;
      }
    }
    if (hitPlayer) continue;

    active.push(updated);
  }

  state.projectiles = active;
}

function updateCoinCollection(state: SimState): void {
  const playersById = [...state.players].sort((a, b) => a.id - b.id);

  for (const coin of state.coins) {
    if (coin.collected) continue;

    const overlappingPlayers = playersById.filter((player) => {
      if (player.health <= 0) return false;
      return overlaps(playerRect(player), coin);
    });

    if (overlappingPlayers.length === 0) continue;

    const collector = overlappingPlayers[0];
    coin.collected = true;
    coin.collectedByPlayerId = collector.id;
    collector.coinCount++;
  }
}

export function resolveWinState(state: SimState): WinResolution | null {
  const goal = goalRect(state);
  const alivePlayers = state.players.filter((player) => player.health > 0).sort((a, b) => a.id - b.id);

  const objectiveWinners = state.players
    .filter((player) => player.health > 0)
    .filter((player) => player.coinCount >= state.requiredCoinsToLaunch)
    .filter((player) => overlaps(playerRect(player), goal))
    .map((player) => player.id)
    .sort((a, b) => a - b);

  const eliminationWinner = alivePlayers.length === 1 ? alivePlayers[0].id : null;
  const eliminationDraw = alivePlayers.length === 0;

  // Same-frame conflicting outcomes resolve as draw.
  if (objectiveWinners.length >= 2) {
    return { status: 'draw', winnerId: null, winReason: 'draw' };
  }

  if (objectiveWinners.length === 1) {
    const objectiveWinner = objectiveWinners[0];
    if (!eliminationDraw && eliminationWinner === null) {
      return { status: 'won', winnerId: objectiveWinner, winReason: 'rocket' };
    }
    if (eliminationWinner === objectiveWinner) {
      return { status: 'won', winnerId: objectiveWinner, winReason: 'rocket' };
    }
    return { status: 'draw', winnerId: null, winReason: 'draw' };
  }

  if (eliminationWinner !== null) {
    return { status: 'won', winnerId: eliminationWinner, winReason: 'elimination' };
  }
  if (eliminationDraw) {
    return { status: 'draw', winnerId: null, winReason: 'draw' };
  }

  return null;
}

function initialPlayer(id: number, x: number, y: number): PlayerSimState {
  return {
    id,
    x,
    y,
    width: PLAYER_WIDTH,
    height: PLAYER_HEIGHT,
    velocityX: 0,
    velocityY: 0,
    isGrounded: false,
    isFacingRight: id === 0,
    health: MAX_HEALTH,
    maxHealth: MAX_HEALTH,
    jetpackFuel: JETPACK_MAX_FUEL,
    maxJetpackFuel: JETPACK_MAX_FUEL,
    coyoteTimerTicks: 0,
    jumpBufferTimerTicks: 0,
    gravityTickCounter: 0,
    laserCooldownTicks: 0,
    jetpackThrustTickCounter: 0,
    jetpackFuelRegenTickCounter: 0,
    prevJumpHeld: false,
    prevFireHeld: false,
    coinCount: 0,
  };
}

function initialCoins(level: LevelData): CoinSimState[] {
  return level.coins.map((coin, index) => ({
    id: `coin_${index}`,
    x: coin.x,
    y: coin.y,
    width: COIN_WIDTH,
    height: COIN_HEIGHT,
    collected: false,
    collectedByPlayerId: null,
  }));
}

export function createInitialSimState(level: LevelData = level1): SimState {
  const spawnLeft = PLAYER_START_LEFT;
  const spawnRightX = mirrorX(spawnLeft.x, level.width, PLAYER_WIDTH);

  return {
    frame: 0,
    stageWidth: level.width,
    stageHeight: level.height,
    requiredCoinsToLaunch: REQUIRED_COINS_TO_LAUNCH,
    players: [
      initialPlayer(0, spawnLeft.x, spawnLeft.y),
      initialPlayer(1, spawnRightX, spawnLeft.y),
    ],
    platforms: level.platforms.map((platform) => ({ ...platform })),
    goal: {
      x: level.goal.x,
      y: level.goal.y - GOAL_HEIGHT,
      width: GOAL_WIDTH,
      height: GOAL_HEIGHT,
    },
    coins: initialCoins(level),
    projectiles: [],
    nextProjectileId: 1,
    match: {
      status: 'playing',
      winnerId: null,
      winReason: null,
      endedFrame: null,
    },
  };
}

export function cloneSimState(state: SimState): SimState {
  return JSON.parse(JSON.stringify(state)) as SimState;
}

export class MoonDuelSim {
  state: SimState;

  constructor(initialState: SimState = createInitialSimState()) {
    this.state = cloneSimState(initialState);
  }

  getState(): SimState {
    return cloneSimState(this.state);
  }

  serialize(): SimState {
    return cloneSimState(this.state);
  }

  deserialize(value: SimState): void {
    this.state = cloneSimState(value);
  }

  tick(playerInputs: Map<number, FrameInputState>): void {
    if (this.state.match.status !== 'playing') return;

    // Stable player-id order removes iteration nondeterminism between peers.
    const inputs = this.state.players
      .map((player) => {
        const fallback: FrameInputState = {
          left: false,
          right: false,
          jumpHeld: false,
          fireHeld: false,
          jetpackHeld: false,
        };
        return {
          player,
          input: playerInputs.get(player.id) ?? fallback,
        };
      })
      .sort((a, b) => a.player.id - b.player.id);

    for (const entry of inputs) {
      updatePlayerMovementAndActions(this.state, entry.player, entry.input);
    }

    updateProjectiles(this.state);
    updateCoinCollection(this.state);

    const winResolution = resolveWinState(this.state);
    if (winResolution) {
      this.state.match = {
        status: winResolution.status,
        winnerId: winResolution.winnerId,
        winReason: winResolution.winReason,
        endedFrame: this.state.frame,
      };
    }

    this.state.frame++;
  }

  reset(): void {
    this.state = createInitialSimState();
  }
}
